/**
 * Solution categories (use case, industry, role, …).
 *
 * To add one:
 * 1. Append an entry here (`value` is the Tina template name, `indexUrl` is the public prefix).
 * 2. Add `content/solution-index-pages/<indexUrl>.mdx` with `_template: <value>Index`.
 * 3. Add a `beforeFiles` rewrite and a `/solution-pages/<indexUrl>` redirect in `next.config.js`.
 * 4. Run Tina codegen (`tinacms dev` or `tinacms build`).
 *
 * Pages live at `app/(solutions)/solution-pages/[solutionType]`. Public URLs
 * stay `/${indexUrl}` and `/${indexUrl}/${slug}` via the rewrite. Do not reuse
 * a resource index URL (`blog`, `articles`, …); the check below fails the build if you do.
 */
import { RESOURCE_TYPES } from "./resource-types";

export interface SolutionCategory {
  /** Tina template name on the solutions collection. */
  value: string;
  /** Public index path segment, e.g. `industries` → `/industries`. */
  indexUrl: string;
  labels: {
    singular: string;
    plural: string;
  };
}

export const SOLUTION_CATEGORIES: SolutionCategory[] = [
  {
    value: "useCase",
    indexUrl: "solutions",
    labels: {
      singular: "Use Case",
      plural: "Solutions",
    },
  },
  {
    value: "industry",
    indexUrl: "industries",
    labels: {
      singular: "Industry",
      plural: "Industries",
    },
  },
  {
    value: "role",
    indexUrl: "roles",
    labels: {
      singular: "Role",
      plural: "Roles",
    },
  },
];

const resourceIndexUrls = new Set(
  RESOURCE_TYPES.map((type) => type.indexUrl)
);
const seenIndexUrls = new Set<string>();

for (const category of SOLUTION_CATEGORIES) {
  if (seenIndexUrls.has(category.indexUrl)) {
    throw new Error(
      `Duplicate solution category indexUrl "${category.indexUrl}"`
    );
  }
  seenIndexUrls.add(category.indexUrl);

  if (resourceIndexUrls.has(category.indexUrl)) {
    throw new Error(
      `Solution category indexUrl "${category.indexUrl}" collides with a resource type`
    );
  }
}

export function getSolutionCategory(
  value: string | null | undefined
): SolutionCategory | undefined {
  if (!value) return undefined;
  return SOLUTION_CATEGORIES.find((category) => category.value === value);
}

export function getSolutionCategoryByIndexUrl(
  indexUrl: string | null | undefined
): SolutionCategory | undefined {
  if (!indexUrl) return undefined;
  return SOLUTION_CATEGORIES.find((category) => category.indexUrl === indexUrl);
}

/** Tina template name for that category's index document. */
export function solutionIndexTemplateName(value: string): string {
  return `${value}Index`;
}
